import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { 
  Camera, 
  Users, 
  Video, 
  Palette,
  ChevronDown,
  ChevronRight,
  ArrowLeft
} from 'lucide-react';
import SEO, { getBreadcrumbSchema } from '../../components/SEO';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * PRICING CATEGORIES - Premium 2-step visual card experience
 * Step 1: 4 large category cards with icons
 * Step 2: Expanded sub-options panel with pill buttons
 * 
 * Guardrails:
 * - Fallback if category has no sub-options
 * - Default route safety if navigation target undefined
 * - Session persistence for category selection
 * ═══════════════════════════════════════════════════════════════════════════
 */

const SESSION_KEY = 'pricingActiveCategory';

const PricingCategories = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeCategory, setActiveCategory] = useState(null);
  const subOptionsRef = useRef(null);
  const isInitialMount = useRef(true);

  // Category data with icons and descriptions
  const categories = [
    {
      id: 'real-estate',
      title: 'Real Estate Media',
      description: 'Premium photography, video tours & marketing packages for property listings.',
      icon: Camera,
      route: '/pricing/real-estate',
      items: [
        { label: 'Real Estate Photography', hash: '#packages' },
        { label: 'Video Tours', hash: '#video-tours' },
        { label: 'Aerial Photos & Video', hash: '#aerial' },
        { label: 'Floor Plans', hash: '#floor-plans' },
        { label: 'Virtual Staging & Renovation', hash: '#virtual-staging-renovation' },
        { label: '3D Tours', hash: '#realsee-3d' },
      ]
    },
    {
      id: 'portraits',
      title: 'Portraits & Photography',
      description: 'Intimate portrait sessions, headshots, and lifestyle photography.',
      icon: Users,
      route: '/pricing/portraits',
      items: [
        { label: 'Private Sessions', hash: '#private-sessions' },
        { label: 'Headshots & Portraits', hash: '#headshots' },
        { label: 'Couples & Engagements', hash: '#couples' },
        { label: 'Pet Photography', hash: '#pets' },
        { label: 'Events & Lifestyle', hash: '#events' },
      ]
    },
    {
      id: 'video-brand',
      title: 'Video & Brand Content',
      description: 'Cinematic brand videos, reels, and social media content.',
      icon: Video,
      route: '/pricing/video-brand',
      items: [
        { label: 'Brand Videos', hash: '#brand-videos' },
        { label: 'Short-Form Content', hash: '#short-form' },
        { label: 'Marketing Reels', hash: '#reels' },
        { label: 'Vertical Social Videos', hash: '#vertical' },
      ]
    },
    {
      id: 'creative-digital',
      title: 'Creative Studio & Digital',
      description: 'AI-powered restoration, digital personas, and custom artwork.',
      icon: Palette,
      route: '/pricing/creative-digital',
      items: [
        { label: 'Digital Persona Development', fullRoute: '/services/digital-persona-development' },
        { label: 'AI Upscaling & Restoration', hash: '#restoration' },
        { label: 'Creative Retouching', hash: '#retouching' },
        { label: 'Custom Artwork & Prints', hash: '#artwork' },
        { label: 'Website Design', hash: '#web' },
      ]
    },
  ];

  // Restore category from sessionStorage or location.state on mount
  useEffect(() => {
    const stateCategory = location.state?.fromPricingCategory;
    const sessionCategory = sessionStorage.getItem(SESSION_KEY);
    
    if (stateCategory && categories.some(c => c.id === stateCategory)) {
      setActiveCategory(stateCategory);
      // Clear the state to prevent re-triggering
      window.history.replaceState({}, document.title);
    } else if (sessionCategory && categories.some(c => c.id === sessionCategory)) {
      setActiveCategory(sessionCategory);
    }
    
    isInitialMount.current = false;
  }, []);

  // Persist selection to sessionStorage
  useEffect(() => {
    if (activeCategory) {
      sessionStorage.setItem(SESSION_KEY, activeCategory);
    } else {
      sessionStorage.removeItem(SESSION_KEY);
    }
  }, [activeCategory]);

  // Scroll to sub-options when category is selected (skip on initial mount restore)
  useEffect(() => {
    if (!isInitialMount.current && activeCategory && subOptionsRef.current) {
      setTimeout(() => {
        subOptionsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [activeCategory]);

  // Handle category card click
  const handleCategoryClick = (categoryId) => {
    if (activeCategory === categoryId) {
      setActiveCategory(null);
    } else {
      setActiveCategory(categoryId);
    }
  };

  // Handle sub-option click - navigate to route with hash (with state for back navigation)
  const handleSubOptionClick = (item, categoryRoute) => {
    // If item has a fullRoute, use it directly (bypasses category route)
    if (item.fullRoute) {
      navigate(item.fullRoute, {
        state: { fromPricingCategory: activeCategory }
      });
      return;
    }
    
    // Otherwise use category route + hash
    const safeRoute = categoryRoute || '/pricing';
    const safeHash = item.hash || '';
    
    navigate(`${safeRoute}${safeHash}`, {
      state: { fromPricingCategory: activeCategory }
    });
  };

  // Handle View All click
  const handleViewAll = (route) => {
    const safeRoute = route || '/pricing';
    navigate(safeRoute, {
      state: { fromPricingCategory: activeCategory }
    });
  };

  // Reset selection
  const handleReset = () => {
    setActiveCategory(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Get active category data
  const activeCategoryData = categories.find(c => c.id === activeCategory);
  
  // Check if active category has sub-options
  const hasSubOptions = activeCategoryData?.items && activeCategoryData.items.length > 0;

  return (
    <div className="flex flex-col min-h-screen overflow-x-hidden bg-[#050816]">
      {/* SEO Meta Tags */}
      <SEO
        title="Pricing & Packages"
        description="Transparent pricing for premium photography, video production, and AI media services. Real estate packages from $199. Serving Arizona & nationwide."
        path="/pricing"
        jsonLd={getBreadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Pricing', path: '/pricing' }
        ])}
      />

      {/* Hero Section */}
      <section className="text-white pt-28 pb-8 md:pt-32 md:pb-12">
        <div className="container mx-auto px-6 max-w-7xl">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm mb-8" aria-label="Breadcrumb">
            <Link 
              to="/" 
              className="text-gray-500 hover:text-gray-300 transition-colors"
              data-testid="breadcrumb-home"
            >
              Home
            </Link>
            <ChevronRight className="w-3.5 h-3.5 text-gray-600" />
            <span className="text-gray-300 font-medium">Pricing</span>
          </nav>
          
          {/* Hero Content */}
          <div className="text-center max-w-[1000px] mx-auto">
            <h1 className="font-serif font-semibold text-4xl md:text-6xl mb-6">
              Pricing & Packages
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Transparent pricing for premium visual media. Select a category to explore services.
            </p>
          </div>
        </div>
      </section>

      {/* Category Cards Section */}
      <section className="pb-8 md:pb-12">
        <div className="container mx-auto px-6 sm:px-8 lg:px-12 max-w-7xl">
          {/* 4-Column Card Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
            {categories.map((category) => {
              const Icon = category.icon;
              const isActive = activeCategory === category.id;
              
              return (
                <button
                  key={category.id}
                  onClick={() => handleCategoryClick(category.id)}
                  data-testid={`pricing-card-${category.id}`}
                  className={`
                    group relative text-left p-6 lg:p-7 rounded-2xl 
                    border transition-all duration-300 ease-out
                    min-h-[260px] flex flex-col
                    ${isActive 
                      ? 'bg-[#F5C056]/10 border-[#F5C056]/50 shadow-lg shadow-[#F5C056]/10' 
                      : 'bg-white/[0.03] border-white/10 hover:bg-white/[0.06] hover:border-white/20 hover:-translate-y-1 hover:shadow-xl hover:shadow-black/20'
                    }
                  `}
                >
                  {/* Icon */}
                  <div className={`
                    w-12 h-12 rounded-xl flex items-center justify-center mb-5 transition-all duration-300
                    ${isActive 
                      ? 'bg-[#F5C056] text-[#050816]' 
                      : 'bg-white/10 text-[#F5C056] group-hover:bg-[#F5C056]/20'
                    }
                  `}>
                    <Icon className="w-6 h-6" />
                  </div>
                  
                  {/* Category Label */}
                  <p className="text-[#F5C056] text-[10px] font-bold uppercase tracking-[0.2em] mb-2">
                    {category.id.replace(/-/g, ' ')}
                  </p>
                  
                  {/* Title */}
                  <h3 className="font-serif text-xl text-white mb-3 group-hover:text-white/90 transition-colors">
                    {category.title}
                  </h3>
                  
                  {/* Description */}
                  <p className="text-sm text-gray-400 leading-relaxed mb-5 flex-grow">
                    {category.description}
                  </p>
                  
                  {/* CTA Button */}
                  <div className={`
                    inline-flex items-center gap-2 text-sm font-medium transition-all duration-300 mt-auto
                    ${isActive 
                      ? 'text-[#F5C056]' 
                      : 'text-[#F5C056]/80 group-hover:text-[#F5C056]'
                    }
                  `}>
                    <span>{isActive ? 'Selected' : 'Explore'}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isActive ? 'rotate-180' : ''}`} />
                  </div>

                  {/* Active Indicator */}
                  {isActive && (
                    <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-4 h-4 bg-[#050816] border-l border-t border-[#F5C056]/50 rotate-45" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Expanded Sub-Options Panel */}
      {activeCategory && activeCategoryData && (
        <section 
          ref={subOptionsRef}
          className="pb-16 md:pb-24 animate-in fade-in slide-in-from-top-4 duration-500"
        >
          <div className="container mx-auto px-6 sm:px-8 lg:px-12 max-w-7xl">
            {/* Panel Container */}
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-8 md:p-10 lg:p-12">
              {/* Panel Header */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div>
                  <p className="text-[#F5C056] text-xs font-bold uppercase tracking-[0.2em] mb-2">
                    {activeCategoryData.title}
                  </p>
                  <h3 className="font-serif text-2xl md:text-3xl text-white">
                    Choose a Service
                  </h3>
                </div>
                <button
                  onClick={handleReset}
                  className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors group"
                  data-testid="pricing-reset-btn"
                >
                  <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                  <span>Choose different category</span>
                </button>
              </div>

              {/* Sub-Option Pills OR Fallback */}
              {hasSubOptions ? (
                <div className="flex flex-wrap gap-3">
                  {activeCategoryData.items.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => handleSubOptionClick(item, activeCategoryData.route)}
                      data-testid={`pricing-suboption-${item.label.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`}
                      className="
                        group/pill px-5 py-3 rounded-full text-sm font-medium
                        bg-white/[0.05] border border-white/10 text-gray-300
                        hover:bg-[#F5C056]/10 hover:border-[#F5C056]/40 hover:text-white
                        active:scale-95
                        transition-all duration-200 ease-out
                        flex items-center gap-2 min-h-[48px]
                      "
                    >
                      <span className="text-[13px] sm:text-sm">{item.label}</span>
                      <ChevronRight className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover/pill:opacity-100 group-hover/pill:translate-x-0 transition-all duration-200 text-[#F5C056]" />
                    </button>
                  ))}
                </div>
              ) : (
                /* Fallback if no sub-options */
                <div className="text-center py-8" data-testid="pricing-no-suboptions-fallback">
                  <p className="text-gray-400 text-lg mb-6">
                    More services coming soon — view all pricing
                  </p>
                </div>
              )}

              {/* View All Link */}
              <div className="mt-8 pt-6 border-t border-white/10">
                <button
                  onClick={() => handleViewAll(activeCategoryData.route)}
                  className="
                    inline-flex items-center gap-2 px-6 py-3 rounded-xl
                    bg-[#F5C056] text-[#050816] font-medium text-sm
                    hover:bg-[#F5C056]/90 hover:-translate-y-0.5
                    active:scale-95
                    transition-all duration-200 shadow-lg shadow-[#F5C056]/20
                  "
                  data-testid="pricing-view-all-btn"
                >
                  View All {activeCategoryData.title} Pricing
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Bottom Accent - Only show when no category selected */}
      {!activeCategory && (
        <section className="pb-24">
          <div className="container mx-auto px-6 sm:px-8 lg:px-12 max-w-7xl">
            <div className="pt-8 border-t border-white/10 text-center">
              <p className="text-gray-500 text-sm">
                Not sure which service you need?{' '}
                <Link to="/contact" className="text-[#F5C056] hover:text-[#F5C056]/80 font-medium transition-colors">
                  Contact us
                </Link>
                {' '}for a personalized recommendation.
              </p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default PricingCategories;
